import React from 'react';
import SectionWrapper from './SectionWrapper';
import WhyUsGrid from './WhyUsGrid';
import StepCards from './StepCards';
import FaqAccordion from './FaqAccordion';
import StructuredData from './StructuredData';
import {
  IconClock, IconShield, IconVerified, IconWrench, IconLock, IconCar,
  IconEye, IconDoc, IconThumbsUp, IconRupee,
} from './icons';
import {
  HEADLINE,
  HEADLINE_SUB,
  TRUST_STRIP,
  STARTING_PRICE,
  WARRANTY,
  MECHANIC_VETTING,
  DOORSTEP_GUARANTEE,
  PARTS_AND_INVOICE,
  PAYMENT_SECURITY,
} from '../../../constants/trust';

/**
 * Above-the-fold trust + price-first band for /service and /booking.
 *
 * SSR-safe by construction: pure render from imported constants — no window
 * access, no effects, no API. It is therefore in the server-rendered HTML via
 * renderToString and hydrates byte-identical (no CLS, no mismatch). The vehicle
 * picker below it is untouched.
 *
 * Brand: evolve-don't-replace — uses only the existing premium/ design system
 * and the core red (#f42222). No new theme.
 *
 * Copy: ONLY owner-confirmed claims from constants/trust.js. Any value that is
 * not owner-confirmed is null in the constants and is render-gated here — never
 * fabricated.
 */
const scrollToPicker = (e) => {
  e.preventDefault();
  if (typeof document === 'undefined') return;
  const el =
    document.getElementById('vehicle-picker') ||
    document.querySelector('.vehicle-selection-row') ||
    document.querySelector('.vehicle-services-header');
  if (el && el.scrollIntoView) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const ServiceTrustBlock = ({ section }) => {
  // section: 'hero' = price/trust hero band only; 'details' = why-us + steps + FAQ only;
  // undefined = render everything (backward-compatible). Lets /service place the picker
  // directly under the hero and move the heavy trust content below the results.
  const showHero = !section || section === 'hero';
  const showDetails = !section || section === 'details';

  // Why-us cards — every card states an owner-confirmed claim only.
  const whyItems = [
    {
      icon: <IconClock />,
      title: '30-minute arrival',
      desc: 'A verified mechanic reaches you in 30 minutes — at your doorstep.',
    },
    {
      icon: <IconShield />,
      title: `${WARRANTY.serviceDays}-day + ${WARRANTY.engineMonths}-month warranty`,
      desc: `${WARRANTY.serviceDays}-day service warranty and ${WARRANTY.engineMonths}-month engine-work warranty. Issue? ${WARRANTY.claimPath.join(' → ')}.`,
    },
    {
      icon: <IconVerified />,
      title: 'Verified mechanics',
      desc: MECHANIC_VETTING.steps.join(' · '),
    },
    {
      icon: <IconWrench />,
      title: PARTS_AND_INVOICE.genuinePartsOnly,
      desc: `Only genuine spare parts are fitted. ${PARTS_AND_INVOICE.gstInvoice}.`,
    },
    {
      icon: <IconLock />,
      title: 'Secure & accountable',
      desc: `${PAYMENT_SECURITY.methods}. ${PAYMENT_SECURITY.grievance}${
        PAYMENT_SECURITY.grievanceSlaText ? ` — ${PAYMENT_SECURITY.grievanceSlaText}` : ''
      }.`,
    },
    {
      icon: <IconCar />,
      title: 'Your vehicle is 100% secure',
      desc: PAYMENT_SECURITY.vehicleSecurity,
    },
  ];

  // Doorstep guarantee as ordered steps.
  const guaranteeSteps = DOORSTEP_GUARANTEE.map((title, i) => ({
    no: i + 1,
    icon: [<IconEye key="s1" />, <IconDoc key="s2" />, <IconThumbsUp key="s3" />, <IconRupee key="s4" />][i] || null,
    title,
    desc: '',
  }));

  // FAQ — only questions answerable from owner-confirmed claims.
  const faqs = [
    {
      q: 'How fast does a mechanic reach me?',
      a: 'A verified Ride N Repair mechanic reaches you in 30 minutes at your doorstep.',
    },
    {
      q: 'What if the issue is not fixed or I am not satisfied?',
      a: `Every service carries a ${WARRANTY.serviceDays}-day warranty (and a ${WARRANTY.engineMonths}-month warranty on engine work). ${WARRANTY.claimPath.join(' → ')}.`,
    },
    {
      q: 'Will there be surprise charges?',
      a: 'No. The repair happens in front of you. We share a written estimate and confirm it with you before any final work — nothing extra is done without your approval, and you pay only what you approved.',
    },
    {
      q: 'Are the spare parts genuine?',
      a: 'Yes — genuine spare parts only, and you receive a GST invoice on every service.',
    },
    {
      q: 'Are the mechanics verified?',
      a: `Yes. ${MECHANIC_VETTING.steps.join(', ')}.`,
    },
    {
      q: 'Is my payment safe?',
      a: `${PAYMENT_SECURITY.methods}. ${PAYMENT_SECURITY.grievance}. ${PAYMENT_SECURITY.vehicleSecurity}.`,
    },
  ];

  // JSON-LD: Service + BreadcrumbList (Ads landing-page relevance + rich
  // results). Built only from owner-confirmed data; FAQPage schema is emitted
  // separately by <FaqAccordion withSchema/> below. AggregateRating is already
  // global and matches the visible rating — not duplicated here.
  const schema = [
    {
      '@type': 'Service',
      serviceType: 'Doorstep bike and car service and repair',
      name: 'Ride N Repair Doorstep Vehicle Service',
      provider: { '@type': 'LocalBusiness', name: 'Ride N Repair' },
      areaServed: { '@type': 'Country', name: 'India' },
      offers: [
        {
          '@type': 'Offer',
          name: 'Doorstep bike service',
          price: String(STARTING_PRICE.bike),
          priceCurrency: 'INR',
        },
        {
          '@type': 'Offer',
          name: 'Doorstep car service',
          price: String(STARTING_PRICE.car),
          priceCurrency: 'INR',
        },
      ],
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://www.ridenrepair.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Services',
          item: 'https://www.ridenrepair.com/service',
        },
      ],
    },
  ];

  return (
    <section className="rr-service-trust" aria-label="Why book with Ride N Repair">
      {showHero && <StructuredData data={schema} />}
      {/* Hero band */}
      {showHero && (
      <div className="rr-service-trust__hero">
        <div className="rr-container">
          <h1 className="rr-service-trust__headline">{HEADLINE}</h1>
          <p className="rr-service-trust__sub">{HEADLINE_SUB}</p>

          <ul className="rr-service-trust__strip">
            {TRUST_STRIP.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          <p className="rr-service-trust__price">
            Bike service from <strong>₹{STARTING_PRICE.bike}</strong> · Car
            service from <strong>₹{STARTING_PRICE.car}</strong> · transparent,
            no hidden charges
          </p>

          <a
            href="#vehicle-picker"
            className="rr-cta-primary rr-service-trust__cta"
            onClick={scrollToPicker}
          >
            Choose your vehicle
          </a>
        </div>
      </div>
      )}

      {/* Why customers trust us */}
      {showDetails && (
      <>
      <SectionWrapper
        kicker="Trusted by 2,00,000+ customers"
        heading="Why Ride N Repair"
        subtitle="Every promise below is something we already honour on every booking."
      >
        <WhyUsGrid items={whyItems} />
      </SectionWrapper>

      {/* The doorstep guarantee */}
      <SectionWrapper
        variant="gray"
        kicker="Our doorstep promise"
        heading="Repaired in front of you"
      >
        <StepCards steps={guaranteeSteps} variant="light" />
      </SectionWrapper>

      {/* FAQ */}
      <SectionWrapper kicker="Good to know" heading="Your questions, answered" narrow>
        <FaqAccordion faqs={faqs} withSchema />
      </SectionWrapper>
      </>
      )}
    </section>
  );
};

export default ServiceTrustBlock;
